// Sends a document DELETE and writes the returned job preferences back, to the
// signed-in user's own copy when it is them and to the company list either way.
async function deleteUserDocument(context, payloadUserId, path) {
  const token = context.rootGetters["authData/token"];
  const targetUserId = payloadUserId || context.state.userId;
  let response;
  let httpStatus;

  try {
    const res = await fetch(process.env.VUE_APP_NODE_SERVER + path, {
      method: "DELETE",
      mode: "cors",
      headers: {
        Authorization: "Bearer " + token,
      },
    });
    httpStatus = res.status;
    response = await res.json();
  } catch (err) {
    return { status: 500 };
  }

  if (httpStatus !== 200) {
    return { status: httpStatus, message: response?.message };
  }

  if (response.jobPreferences) {
    if (targetUserId == context.state.userId) {
      context.commit("jobPreferences", response.jobPreferences);
    }

    context.commit("companyUserPreferences", {
      userId: targetUserId,
      jobPreferences: response.jobPreferences,
    });
  }

  return { status: 200 };
}

export default {
  // GET Request //

  // Gets all user data
  async getUserData(context, userId) {
    const token = context.rootGetters["authData/token"];
    let response;
    let httpStatus;

    try {
      const res = await fetch(
        process.env.VUE_APP_NODE_SERVER + "/user/retrieve/" + userId,
        {
          method: "GET",
          headers: { Authorization: "Bearer " + token },
        },
      );
      httpStatus = res.status;
      response = await res.json();
    } catch (err) {
      return { status: 500 };
    }

    if (httpStatus !== 200) {
      return { status: httpStatus };
    }

    context.commit("userData", {
      userId: response.userId,
      firstName: response.firstName,
      lastName: response.lastName,
      userType: response.type,
      companyId: response.companyId,
      emailAddress: response.emailAddress,
      roleId: response.roleId,
      profilePicture: response.profilePicture || null,
    });
    context.commit("userConfig", {
      selectedCulture: response.uiConfig?.language,
      termsAccepted: response.uiConfig?.termsAcceptedDate,
      companyTitle: response.companies?.[0]?.companyTitle,
      managerId: response.companies?.[0]?.managerId,
      locationId: response.companies?.[0]?.locationId,
      departments: response.companies?.[0]?.departments,
    });
    context.commit("jobPreferences", response.jobPreferences);
    context.commit("companies", response.companies);
    context.commit("userTimestamp");

    let companyIds = [];
    for (let i = 0; i < response.companies.length; i++) {
      companyIds.push(response.companies[i].companyId);
    }

    return {
      status: 200,
      userId: response.userId,
      companyIds: companyIds,
    };
  },
  // Gets all company data
  async getCompanyData(context, companyId) {
    const token = context.rootGetters["authData/token"];
    let response;
    const companyIdentifier = companyId || context.state.company.companyId;
    try {
      response = await fetch(
        process.env.VUE_APP_NODE_SERVER +
          "/company/retrieve/" +
          companyIdentifier,
        {
          method: "GET",
          headers: { Authorization: "Bearer " + token },
        },
      );
      response = await response.json();
    } catch (err) {
      return { status: 500 };
    }

    context.commit("companyData", {
      companyId: response.company.companyId,
      companyName: response.company.companyName,
      companyType: response.company.companyType,
      headOfficeAddress: {
        streetAddress: response.company.streetAddress,
        city: response.company.city,
        state: response.company.state,
        zipcode: response.company.zipcode,
        phoneNumber: response.company.phoneNumber,
      },
      companyDomain: response.company.companyDomain,
      allowAddUsers: response.company.allowAddUser,
      primaryUser: response.company.primaryUserId,
      businessNumber: response.company.businessNumber,
      countryCode: response.company.countryCode,
    });

    context.commit("companyConfig", {
      registrationComplete: response.config.uiConfig.registrationComplete,
      features: response.config.features,
      recruitment: response.config.recruitment,
    });
    context.commit("theme", response.config.theme || null);

    let payloadLocations = [];
    response.config.locations.forEach((location) => {
      payloadLocations.push({
        locationId: location.locationId,
        locationName: location.locationName,
        streetAddress: location.streetAddress,
        city: location.city,
        state: location.state,
        zipcode: location.zipcode,
        country: location.countryCode,
        geolocation: location.geolocation,
        chatEnabled: location.chatEnabled,
        chatCarrier: location.chatCarrier,
        chatAddress: location.chatAddress,
      });
    });

    context.commit("companyLocations", {
      locations: payloadLocations,
    });

    let payloadDepartments = [];
    response.config.departments.forEach((department) => {
      payloadDepartments.push({
        departmentId: department.departmentId,
        departmentName: department.departmentName,
        managerId: department.managerId || null,
      });
    });

    context.commit("companyDepartments", {
      departments: payloadDepartments,
    });

    context.commit(
      "appDefault/availableRoles",
      {
        availableRoles: response.availableRoles,
      },
      { root: true },
    );

    context.commit("companyTimestamp");

    return { status: 200 };
  },
  // Gets all customers that belong to a company
  async getCustomersByCompany(context) {
    const token = context.rootGetters["authData/token"];
    let users;
    const companyId = context.state.company.companyId;

    try {
      users = await fetch(
        process.env.VUE_APP_NODE_SERVER +
          "/user/retrieve-by-company-id/" +
          companyId,
        {
          method: "GET",
          headers: { Authorization: "Bearer " + token },
        },
      );

      users = await users.json();
    } catch (err) {
      return 500;
    }

    // Find supervising managers
    let managerIds = [];
    managerIds.push(context.state.userId);

    let managers = [context.state.userId];
    while (managers.length != 0) {
      let usersFound = [];
      for (let i = 0; i < managers.length; i++) {
        for (let j = 0; j < users.length; j++) {
          if (users[j].managerId == managers[i]) {
            managerIds.push(users[j].userId);
            usersFound.push(users[j].userId);
          }
        }
      }
      managers = usersFound;
    }

    // Add company users
    let usersPayload = [];
    users.forEach((user) => {
      usersPayload.push({
        userId: user.userId,
        companyId: companyId,
        firstName: user.firstName,
        lastName: user.lastName,
        fullName: user.firstName + " " + user.lastName,
        emailAddress: user.emailAddress,
        roleId: user.roleId,
        type: user.type,
        phoneNumber: user.phoneNumber,
        profilePicture: user.profilePicture || null,
        companyTitle: user.companyTitle || null,
        managerId: user.managerId || null,
        locationId: user.locationId || null,
        departments: user.departments || [],
        startDate: user.startDate || null,
        points: user.points || 0,
        jobPreferences: user.jobPreferences || null,
        primaryUser: context.state.company.primaryUser == user.userId,
        visible: managerIds.includes(user.managerId || null),
      });
    });

    context.commit("customersInCompany", usersPayload);
    context.commit("companyUsersTimestamp");

    return 200;
  },
  // PATCH Requests //

  // Updates company information
  async patchCompany(context, payload) {
    const token = context.rootGetters["authData/token"];
    let response;
    var bodyContent = {
      companyId: context.state.company.companyId,
    };
    if (payload.companyName) {
      bodyContent["companyName"] = payload.companyName;
    }
    if (payload.businessNumber) {
      bodyContent["businessNumber"] = payload.businessNumber;
    }
    if (payload.streetAddress) {
      bodyContent["streetAddress"] = payload.streetAddress;
      bodyContent["city"] = payload.city;
      bodyContent["state"] = payload.state;
      bodyContent["zipcode"] = payload.zipcode;
    }
    if (payload.phoneNumber) {
      bodyContent["phoneNumber"] = payload.phoneNumber;
    }

    try {
      response = await fetch(
        process.env.VUE_APP_NODE_SERVER + "/company/update",
        {
          method: "PATCH",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify(bodyContent),
        },
      );
      response = await response.json();
    } catch (err) {
      return 500;
    }
    context.commit("companyData", {
      companyId: response.companyId,
      companyName: response.companyName,
      companyType: response.companyType,
      headOfficeAddress: {
        streetAddress: response.headOfficeAddress.streetAddress,
        city: response.headOfficeAddress.city,
        state: response.headOfficeAddress.state,
        zipcode: response.headOfficeAddress.zipcode,
        phoneNumber: response.headOfficeAddress.phoneNumber,
      },
      companyDomain: response.companyDomain,
      allowAddUsers: response.allowAddUser,
      primaryUser: response.primaryUserId,
      businessNumber: response.businessNumber,
    });
    return 200;
  },
  // saves updates user information
  async patchUser(context, payload) {
    const token = context.rootGetters["authData/token"];
    let response;

    try {
      response = await fetch(process.env.VUE_APP_NODE_SERVER + "/user/update", {
        method: "PATCH",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify({
          userId: payload.userId,
          firstName: payload.firstName,
          lastName: payload.lastName,
          phoneNumber: payload.phoneNumber,
          role: payload.role,
        }),
      });
      response = await response.json();
    } catch (err) {
      return 500;
    }

    var usersPayload = context.state.users;
    for (let i = 0; i < usersPayload.length; i++) {
      if (usersPayload[i].userId == response.userId) {
        usersPayload[i].firstName = response.firstName;
        usersPayload[i].lastName = response.lastName;
        usersPayload[i].roleId = response.roleId;
        usersPayload[i].phoneNumber = response.phoneNumber;
        break;
      }
    }
    context.commit("customersInCompany", usersPayload);

    return 200;
  },

  // Updates a specific location
  async patchLocation(context, payload) {
    const token = context.rootGetters["authData/token"];
    let response;
    let bodyContent = {
      companyId: context.state.company.companyId,
      locationId: payload.locationId,
      locationName: payload.locationName || null,
      streetAddress: payload.streetAddress || null,
      city: payload.city || null,
      state: payload.state || null,
      zipcode: payload.zipcode || null,
      countryCode: context.state.company.countryCode || null,
      chatEnabled: payload.chatEnabled || null,
      chatCarrier: payload.chatCarrier || null,
      chatAddress: payload.chatAddress || null,
    };

    try {
      response = await fetch(
        process.env.VUE_APP_NODE_SERVER + "/company/update-location",
        {
          method: "PATCH",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify(bodyContent),
        },
      );
      response = await response.json();
    } catch (err) {
      return 500;
    }

    let payloadLocations = [];
    response.locations.forEach((location) => {
      payloadLocations.push({
        locationId: location.locationId,
        locationName: location.locationName,
        streetAddress: location.streetAddress,
        city: location.city,
        state: location.state,
        zipcode: location.zipcode,
        country: location.countryCode,
        geolocation: location.geolocation,
        chatEnabled: location.chatEnabled,
        chatCarrier: location.chatCarrier,
        chatAddress: location.chatAddress,
      });
    });
    context.commit("companyLocations", {
      locations: payloadLocations,
    });

    return 200;
  },
  // Update company department
  async patchDepartment(context, payload) {
    const token = context.rootGetters["authData/token"];
    const companyId = context.state.company.companyId;
    let response;

    try {
      response = await fetch(
        process.env.VUE_APP_NODE_SERVER + "/company/update-department",
        {
          method: "PATCH",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify({
            companyId: companyId,
            departmentId: payload.departmentId,
            departmentName: payload.departmentName,
            managerId: payload.managerId,
          }),
        },
      );
      response = await response.json();
    } catch (err) {
      return 500;
    }

    // Update users
    if (payload.managerId) {
      var usersPayload = context.state.users;
      for (let i = 0; i < usersPayload.length; i++) {
        if (
          usersPayload[i].userId == payload.managerId &&
          !usersPayload[i].departments.includes(payload.departmentId)
        ) {
          usersPayload[i].departments.push(payload.departmentId);
        }
      }
      context.commit("customersInCompany", usersPayload);
    }

    // Update departments
    let payloadDepartments = [];
    response.departments.forEach((department) => {
      payloadDepartments.push({
        departmentId: department.departmentId,
        departmentName: department.departmentName,
        managerId: department.managerId || null,
      });
    });
    context.commit("companyDepartments", {
      departments: payloadDepartments,
    });
    return 200;
  },
  // saves the user configuration
  async patchSaveUserConfig(context, payload) {
    const token = context.rootGetters["authData/token"];
    const companyId = context.state.company.companyId;
    const userId = context.state.userId;

    let response;
    var bodyContent = {
      userId: payload.userId || userId,
      uiConfig: {},
    };

    if (payload.termsAcceptedDate) {
      bodyContent.uiConfig.termsAcceptedDate = payload.termsAcceptedDate;
    }

    if (payload.language) {
      bodyContent.uiConfig.language = payload.language;
    }

    // Only the plain-JSON preference fields — cv and documentation are file
    // uploads with their own endpoints, and the backend refuses them here.
    if (payload.jobPreferences) {
      bodyContent["jobPreferences"] = payload.jobPreferences;
    }

    if (payload.company) {
      bodyContent["company"] = {
        companyId: companyId,
        managerId: payload.company.managerId,
        companyTitle: payload.company.companyTitle,
        locationId: payload.company.locationId,
        departments: payload.company.departments,
        startDate: payload.company.startDate,
      };
    }

    let httpStatus;

    try {
      const res = await fetch(
        process.env.VUE_APP_NODE_SERVER + "/user/update-configuration",
        {
          method: "PATCH",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify(bodyContent),
        },
      );
      httpStatus = res.status;
      response = await res.json();
    } catch (err) {
      return { status: 500 };
    }

    // An error body carries no `companies`, so bail out before reading it —
    // otherwise a 400 surfaces as "Cannot read properties of undefined".
    if (httpStatus !== 200) {
      return { status: httpStatus, errors: response?.errors };
    }

    let company;
    for (let i = 0; i < response.companies.length; i++) {
      if (response.companies[i].companyId == companyId) {
        company = {
          managerId: response.companies[i].managerId,
          startDate: response.companies[i].startDate,
          companyTitle: response.companies[i].companyTitle,
          locationId: response.companies[i].locationId,
          departments: response.companies[i].departments || [],
        };
        break;
      }
    }

    if (response.userId == userId) {
      context.commit("userConfig", {
        selectedCulture: response.uiConfig.language,
        termsAccepted: response.uiConfig.termsAcceptedDate,
        companyTitle: company.companyTitle,
        managerId: company.managerId,
        departments: company.departments,
      });

      if (response.jobPreferences) {
        context.commit("jobPreferences", response.jobPreferences);
      }
    }

    // Whoever was saved, keep their row in the company list in step — that is
    // what the candidate page and the Marketplace read from.
    if (response.userId && response.jobPreferences) {
      context.commit("companyUserPreferences", {
        userId: response.userId,
        jobPreferences: response.jobPreferences,
      });
    }

    if (payload.company) {
      var usersPayload = context.state.users;
      for (let i = 0; i < usersPayload.length; i++) {
        if (usersPayload[i].userId == response.userId) {
          usersPayload[i].managerId = company.managerId;
          usersPayload[i].startDate = company.startDate;
          usersPayload[i].companyTitle = company.companyTitle;
          usersPayload[i].locationId = company.locationId;
          usersPayload[i].departments = company.departments;
          usersPayload[i].visible = company.managerId
            ? context.getters.companyUsers.find(
                (user) => user.userId == company.managerId,
              ).visible
            : false;
          break;
        }
      }
      context.commit("customersInCompany", usersPayload);
    }
    return { status: 200 };
  },
  // saves the user configuration
  async patchCompanyConfig(context, payload) {
    const token = context.rootGetters["authData/token"];
    let response;
    let httpStatus;
    try {
      const res = await fetch(
        process.env.VUE_APP_NODE_SERVER + "/company/update-configuration",
        {
          method: "PATCH",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify({
            companyId: context.state.company.companyId,
            registrationComplete: payload.registrationComplete || null,
            theme: payload.theme || null,
            // Sent only when the caller is saving recruitment settings. The
            // server REPLACES this block, so the caller passes all of it:
            // industries, capabilityStatementRequired and marketplaceEnabled.
            ...(payload.recruitment !== undefined
              ? { recruitment: payload.recruitment }
              : {}),
          }),
        },
      );
      httpStatus = res.status;
      response = await res.json();
    } catch (err) {
      return 500;
    }

    // Only a 200 carries `config`. Without this check any error response —
    // a 403 from the permission claim, or a 500 whose body is {message} —
    // fell straight through to `response.config.uiConfig` and threw a
    // TypeError out of the action instead of letting the page show its
    // "Update Failed" toast.
    if (httpStatus !== 200 || !response?.config) {
      console.error(
        "patchCompanyConfig failed with status",
        httpStatus,
        response,
      );
      return httpStatus || 500;
    }

    context.commit("companyConfig", {
      registrationComplete: response.config.uiConfig?.registrationComplete,
      features: response.config.features,
      recruitment: response.config.recruitment,
    });
    context.commit("theme", response.config.theme || null);

    return 200;
  },
  // Saves the company's recruitment settings. The server REPLACES the whole
  // `recruitment` block, so every save sends all of it — industries,
  // capabilityStatementRequired and marketplaceEnabled — built from what is
  // in the store, with the caller's changes on top. That way the Marketplace
  // and Onboarding settings pages can never wipe each other's settings.
  async patchRecruitmentSettings(context, changes) {
    const current = context.state.company.recruitment || {};

    return context.dispatch("patchCompanyConfig", {
      recruitment: {
        industries: (current.industries || []).map((entry) => ({
          industryId: entry.industryId,
          documentation: entry.documentation || [],
        })),
        capabilityStatementRequired:
          current.capabilityStatementRequired === true,
        marketplaceEnabled: current.marketplaceEnabled !== false,
        ...changes,
      },
    });
  },
  // saves the user configuration
  // Uploads the user's CV. Multipart, so no Content-Type header — the browser
  // sets the boundary itself.
  async patchUploadCv(context, payload) {
    const token = context.rootGetters["authData/token"];
    let response;
    let httpStatus;

    // `payload.userId` targets another account — company staff keeping a
    // candidate's CV current. Absent, it is the signed-in user, which is how
    // Account > General has always called this.
    const targetUserId = payload.userId || context.state.userId;

    const fd = new FormData();
    fd.append("file", payload.file, payload.file.name);
    fd.append("userId", targetUserId);

    try {
      const res = await fetch(
        process.env.VUE_APP_NODE_SERVER + "/user/upload-cv",
        {
          method: "PATCH",
          mode: "cors",
          headers: {
            Authorization: "Bearer " + token,
          },
          body: fd,
        },
      );
      httpStatus = res.status;
      response = await res.json();
    } catch (err) {
      return { status: 500 };
    }

    if (httpStatus !== 200) {
      return { status: httpStatus, message: response?.message };
    }

    // The response carries the sanitised block, never the stored S3 key.
    if (response.jobPreferences) {
      if (targetUserId == context.state.userId) {
        context.commit("jobPreferences", response.jobPreferences);
      }

      context.commit("companyUserPreferences", {
        userId: targetUserId,
        jobPreferences: response.jobPreferences,
      });
    }

    return { status: 200 };
  },
  // Uploads one documentation file against a caller-supplied documentationId,
  // which identifies which document it satisfies.
  async postUploadDocumentation(context, payload) {
    const token = context.rootGetters["authData/token"];
    let response;
    let httpStatus;

    const targetUserId = payload.userId || context.state.userId;

    const fd = new FormData();
    fd.append("file", payload.file, payload.file.name);
    fd.append("userId", targetUserId);
    fd.append("documentationId", payload.documentationId);

    try {
      const res = await fetch(
        process.env.VUE_APP_NODE_SERVER + "/user/upload-documentation",
        {
          method: "POST",
          mode: "cors",
          headers: {
            Authorization: "Bearer " + token,
          },
          body: fd,
        },
      );
      httpStatus = res.status;
      response = await res.json();
    } catch (err) {
      return { status: 500 };
    }

    if (httpStatus !== 201 && httpStatus !== 200) {
      return { status: httpStatus, message: response?.message };
    }

    if (response.jobPreferences) {
      if (targetUserId == context.state.userId) {
        context.commit("jobPreferences", response.jobPreferences);
      }

      context.commit("companyUserPreferences", {
        userId: targetUserId,
        jobPreferences: response.jobPreferences,
      });
    }

    return { status: 200 };
  },
  // A short-lived download link for one stored document. The backend keeps
  // the S3 key private and signs a URL on demand (NFR-1), so nothing here
  // ever sees a bucket path.
  async getDocumentationLink(context, payload) {
    const token = context.rootGetters["authData/token"];
    const userId = payload.userId || context.state.userId;

    try {
      const res = await fetch(
        process.env.VUE_APP_NODE_SERVER +
          "/user/retrieve-documentation-link/" +
          userId +
          "/" +
          encodeURIComponent(payload.documentationId),
        {
          method: "GET",
          mode: "cors",
          headers: {
            Authorization: "Bearer " + token,
          },
        },
      );

      const response = await res.json();

      if (res.status !== 200) {
        return { status: res.status, message: response?.message };
      }

      return { status: 200, url: response.url || response.link };
    } catch (err) {
      return { status: 500 };
    }
  },
  // Deletes a user's CV, or one documentation entry when `documentationId` is
  // given. The server returns the updated job preferences, written back the
  // same way an upload's are.
  async deleteCv(context, payload) {
    return deleteUserDocument(
      context,
      payload?.userId,
      "/user/delete-cv/" + (payload?.userId || context.state.userId),
    );
  },
  async deleteDocumentation(context, payload) {
    const userId = payload?.userId || context.state.userId;

    return deleteUserDocument(
      context,
      userId,
      "/user/delete-documentation/" +
        userId +
        "/" +
        encodeURIComponent(payload.documentationId),
    );
  },
  // A short-lived download link for a user's CV, signed on demand like
  // getDocumentationLink.
  async getCvLink(context, payload) {
    const token = context.rootGetters["authData/token"];
    const userId = payload?.userId || context.state.userId;

    try {
      const res = await fetch(
        process.env.VUE_APP_NODE_SERVER + "/user/retrieve-cv-link/" + userId,
        {
          method: "GET",
          mode: "cors",
          headers: {
            Authorization: "Bearer " + token,
          },
        },
      );

      const response = await res.json();

      if (res.status !== 200) {
        return { status: res.status, message: response?.message };
      }

      return { status: 200, url: response.url };
    } catch (err) {
      return { status: 500 };
    }
  },
  async patchProfilePicture(context, payload) {
    context.commit("profilePicture", payload.localFile);
    const token = context.rootGetters["authData/token"];
    let session;
    const fd = new FormData();
    fd.append("file", payload.file, context.state.userId);
    fd.append("userId", context.state.userId);

    try {
      session = await fetch(
        process.env.VUE_APP_NODE_SERVER + "/user/update-profile-picture",
        {
          method: "PATCH",
          mode: "cors",
          headers: {
            Authorization: "Bearer " + token,
          },
          body: fd,
        },
      );
      session = await session.json();
    } catch (err) {
      return { status: 500 };
    }

    context.commit("profilePicture", session.profilePicture);

    session.status = 200;
    return session;
  },
  // saves the user configuration
  async patchBrandImage(context, payload) {
    const token = context.rootGetters["authData/token"];
    const companyId = context.state.company.companyId;

    let session;
    const fd = new FormData();
    fd.append("file", payload.file, `${companyId}_${payload.type}.png`);
    fd.append("type", payload.type);
    fd.append("companyId", companyId);

    try {
      session = await fetch(
        process.env.VUE_APP_NODE_SERVER + "/company/update-brand-image",
        {
          method: "PATCH",
          mode: "cors",
          headers: {
            Authorization: "Bearer " + token,
          },
          body: fd,
        },
      );
      session = await session.json();
    } catch (err) {
      return { status: 500 };
    }

    context.commit("theme", session.theme || null);

    console.log(session);

    session.status = 200;
    return session;
  },
  // add points to user account
  async patchAddUserPoints(context, payload) {
    const token = context.rootGetters["authData/token"];
    const companyId = context.state.company.companyId;
    let response;
    try {
      response = await fetch(
        process.env.VUE_APP_NODE_SERVER + "/user/add-points",
        {
          method: "PATCH",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify({
            companyId: companyId,
            userId: payload.userId,
            points: payload.points,
            message: payload.message,
          }),
        },
      );
      response = await response.json();
    } catch (err) {
      return {
        status: 500,
      };
    }

    var usersPayload = context.state.users;
    for (let i = 0; i < usersPayload.length; i++) {
      if (usersPayload[i].userId == response.userId) {
        usersPayload[i].points = response.points;
        break;
      }
    }
    context.commit("customersInCompany", usersPayload);

    return {
      status: 200,
      userId: response.userId,
      points: response.points,
    };
  },
  // POST Requests //

  // Create new user in account
  async postCreateUser(context, payload) {
    const token = context.rootGetters["authData/token"];
    let response;
    var bodyContent = {
      companyId: context.state.company.companyId,
      firstName: payload.firstName,
      lastName: payload.lastName,
      emailAddress: payload.emailAddress,
      phoneNumber: payload.phoneNumber,
      startDate: payload.startDate,
      type: payload.type,
      role: payload.role,
      managerId: payload.managerId || null,
      companyTitle: payload.companyTitle || null,
      locationId: payload.locationId || null,
      departmentId: payload.departmentId || null,
      countryCode: payload.countryCode || context.state.company.countryCode,
    };

    if (payload.companyTitle != "") {
      bodyContent.companyTitle = payload.companyTitle;
    }

    try {
      response = await fetch(process.env.VUE_APP_NODE_SERVER + "/user/create", {
        method: "POST",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify(bodyContent),
      });
      response = await response.json();
    } catch (err) {
      return 500;
    }

    let userObject = {
      userId: response.userId,
      firstName: response.firstName,
      lastName: response.lastName,
      fullName: response.firstName + " " + response.lastName,
      emailAddress: response.emailAddress,
      roleId: response.roleId,
      type: response.type,
      phoneNumber: response.phoneNumber,
      startDate: response.companies[0].startDate,
      managerId: response.companies[0].managerId,
      companyTitle: response.companies[0].companyTitle,
      locationId: response.companies[0].locationId,
      departments: response.companies[0].departments,
      points: response.companies[0].points,
      visible: response.companies[0].managerId
        ? context.getters.companyUsers.find(
            (us) => us.userId == response.companies[0].managerId,
          ).visible
        : false,
      primaryUser: context.state.company.primaryUser == response.userId,
    };

    let updatedUsers = context.state.users;
    updatedUsers.push(userObject);
    context.commit("customersInCompany", updatedUsers);

    return 201;
  },
  // Adds general user to company
  async postAddToCompany(context, payload) {
    const token = context.rootGetters["authData/token"];
    const companyId = context.state.company.companyId;
    let response;
    try {
      response = await fetch(
        process.env.VUE_APP_NODE_SERVER + "/user/add-to-company",
        {
          method: "POST",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify({
            companyId: companyId,
            userId: payload.userId,
            managerId: payload.managerId,
            companyTitle: payload.companyTitle,
            locationId: payload.locationId,
            departmentId: payload.departmentId,
            startDate: payload.startDate,
          }),
        },
      );
      response = await response.json();
    } catch (err) {
      return 500;
    }

    let company;
    for (let i = 0; i < response.companies.length; i++) {
      if (response.companies[i].companyId == companyId) {
        company = {
          managerId: response.companies[i].managerId,
          startDate: response.companies[i].startDate,
          companyTitle: response.companies[i].companyTitle,
          locationId: response.companies[i].locationId,
          departments: response.companies[i].departments,
          points: response.companies[i].points,
        };
        break;
      }
    }

    var updatedUsers = context.state.users;
    updatedUsers.push({
      userId: response.userId,
      firstName: response.firstName,
      lastName: response.lastName,
      fullName: response.firstName + " " + response.lastName,
      emailAddress: response.emailAddress,
      roleId: response.roleId,
      type: response.type,
      phoneNumber: response.phoneNumber || null,
      profilePicture: response.profilePicture || null,
      primaryUser: false,
      managerId: company.managerId,
      startDate: company.startDate,
      companyTitle: company.companyTitle,
      departments: company.departments,
      points: company.points,
      visible: company.managerId
        ? context.getters.companyUsers.find(
            (user) => user.userId == company.managerId,
          ).visible
        : false,
    });

    context.commit("customersInCompany", updatedUsers);
    return 201;
  },
  // Adds company to company configuration
  async postAddLocation(context, payload) {
    const token = context.rootGetters["authData/token"];
    let response;
    var bodyContent = {
      companyId: context.state.company.companyId,
      locationName: payload.locationName,
      streetAddress: payload.streetAddress,
      city: payload.city,
      state: payload.state,
      zipcode: payload.zipcode,
      country: payload.country || context.state.company.countryCode,
      chatEnabled: payload.chatEnabled || null,
      chatCarrier: payload.chatCarrier || null,
      chatAddress: payload.chatAddress || null,
    };
    try {
      response = await fetch(
        process.env.VUE_APP_NODE_SERVER + "/company/add-location",
        {
          method: "POST",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify(bodyContent),
        },
      );
      response = await response.json();
    } catch (err) {
      return 500;
    }

    let payloadLocations = [];
    response.locations.forEach((location) => {
      payloadLocations.push({
        locationId: location.locationId,
        locationName: location.locationName,
        streetAddress: location.streetAddress,
        city: location.city,
        state: location.state,
        zipcode: location.zipcode,
        country: location.countryCode,
        geolocation: location.geolocation,
        chatEnabled: location.chatEnabled,
        chatCarrier: location.chatCarrier,
        chatAddress: location.chatAddress,
      });
    });
    context.commit("companyLocations", {
      locations: payloadLocations,
    });
    return 201;
  },
  // Adds company to company configuration
  async postAddDepartment(context, payload) {
    const token = context.rootGetters["authData/token"];
    const companyId = context.state.company.companyId;
    let response;
    try {
      response = await fetch(
        process.env.VUE_APP_NODE_SERVER + "/company/add-department",
        {
          method: "POST",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify({
            companyId: companyId,
            departmentName: payload.departmentName,
            managerId: payload.managerId,
          }),
        },
      );
      response = await response.json();
    } catch (err) {
      return 500;
    }
    // Update users
    if (payload.managerId) {
      var usersPayload = context.state.users;
      for (let i = 0; i < usersPayload.length; i++) {
        if (usersPayload[i].userId == payload.managerId) {
          usersPayload[i].departments.push(response.departmentId);
        }
      }
      context.commit("customersInCompany", usersPayload);
    }

    // Update departments
    let payloadDepartments = [];
    response.departments.forEach((department) => {
      payloadDepartments.push({
        departmentId: department.departmentId,
        departmentName: department.departmentName,
        managerId: department.managerId || null,
      });
    });
    context.commit("companyDepartments", {
      departments: payloadDepartments,
    });
    return 201;
  },
  // Send Password Reset to email account
  async postPasswordReset(context, emailAddress) {
    const token = context.rootGetters["authData/token"];

    try {
      await fetch(process.env.VUE_APP_NODE_SERVER + "/user/password-reset", {
        method: "POST",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify({
          emailAddress: emailAddress,
        }),
      });
    } catch (err) {
      return 500;
    }

    return 201;
  },
  // DELETE Requests //

  // Delete user from system
  async deleteUser(context, payload) {
    const token = context.rootGetters["authData/token"];
    let response;
    try {
      response = await fetch(
        process.env.VUE_APP_NODE_SERVER + "/user/delete-user/" + payload.userId,
        {
          method: "DELETE",
          mode: "cors",
          headers: { Authorization: "Bearer " + token },
        },
      );
      response = await response.json();

      await fetch(
        process.env.VUE_APP_NODE_MESSENGER_SERVER +
          "/account/remove-user-resources",
        {
          method: "DELETE",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify({
            userId: payload.userId,
          }),
        },
      );
    } catch (err) {
      return 500;
    }

    const updatedUsers = context.state.users.filter(
      (user) => user.userId != response.userId,
    );
    context.commit("customersInCompany", updatedUsers);

    return 201;
  },
  // Remove user from company account
  async deleteRemoveUserFromCompany(context, payload) {
    const token = context.rootGetters["authData/token"];
    let response;
    var bodyContent = {
      companyId: context.state.company.companyId,
      userId: payload.userId,
    };
    try {
      response = await fetch(
        process.env.VUE_APP_NODE_SERVER + "/user/remove-from-company",
        {
          method: "DELETE",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify(bodyContent),
        },
      );
      response = await response.json();

      await fetch(
        process.env.VUE_APP_NODE_MESSENGER_SERVER +
          "/account/remove-user-resources-in-company",
        {
          method: "DELETE",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify({
            companyId: context.state.company.companyId,
            userId: payload.userId,
          }),
        },
      );
    } catch (err) {
      return 500;
    }

    const updatedUsers = context.state.users.filter((updatedUser) => {
      return updatedUser.userId != response.userId;
    });

    context.commit("customersInCompany", updatedUsers);
    return 201;
  },
  // Remove location from company
  async deleteLocation(context, payload) {
    const token = context.rootGetters["authData/token"];
    let response;
    try {
      response = await fetch(
        process.env.VUE_APP_NODE_SERVER + "/company/delete-location",
        {
          method: "DELETE",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify({
            companyId: context.state.company.companyId,
            locationId: payload.locationId,
          }),
        },
      );
      response = await response.json();
    } catch (err) {
      return 500;
    }

    // Update users
    var usersPayload = context.state.users;
    for (let i = 0; i < usersPayload.length; i++) {
      if (usersPayload[i].locationId == payload.locationId) {
        usersPayload[i].locationId = null;
      }
    }
    context.commit("customersInCompany", usersPayload);

    // Update locations
    let payloadLocations = [];
    response.locations.forEach((location) => {
      payloadLocations.push({
        locationId: location.locationId,
        locationName: location.locationName,
        streetAddress: location.streetAddress,
        city: location.city,
        state: location.state,
        zipcode: location.zipcode,
        country: location.countryCode,
        geolocation: location.geolocation,
        chatEnabled: location.chatEnabled,
        chatCarrier: location.chatCarrier,
        chatAddress: location.chatAddress,
      });
    });
    context.commit("companyLocations", {
      locations: payloadLocations,
    });
    return 200;
  },
  // Remove location from company
  async deleteDepartment(context, payload) {
    const token = context.rootGetters["authData/token"];
    let response;
    try {
      response = await fetch(
        process.env.VUE_APP_NODE_SERVER + "/company/delete-department",
        {
          method: "DELETE",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify({
            companyId: context.state.company.companyId,
            departmentId: payload.departmentId,
          }),
        },
      );
      response = await response.json();
    } catch (err) {
      return 500;
    }

    // Update users
    var usersPayload = context.state.users;
    for (let i = 0; i < usersPayload.length; i++) {
      usersPayload[i].departments = usersPayload[i].departments.filter(
        (department) => department != payload.departmentId,
      );
    }
    context.commit("customersInCompany", usersPayload);

    // Update departments
    let payloadDepartments = [];
    response.departments.forEach((department) => {
      payloadDepartments.push({
        departmentId: department.departmentId,
        departmentName: department.departmentName,
        managerId: department.managerId || null,
      });
    });
    context.commit("companyDepartments", {
      departments: payloadDepartments,
    });

    return 201;
  },
  // Updates the terms with date and value
  acceptTerms(context) {
    context.commit("acceptTerms");
  },
  sideMenuLockToggle(context) {
    // toggle is a Bolean with true/false value
    context.commit("sideMenuLockToggle");
  },
  showBannerToggle(context) {
    context.commit("showBannerToggle");
  },
  // Add permissions to user data
  addPermissions(context, payload) {
    context.commit("permissions", payload);
  },
  // Reset company users
  companyUsersTimestampReset(context) {
    context.commit("companyUsersTimestampReset");
  },
};
